import { faEdit, faTrashAlt } from '@fortawesome/free-solid-svg-icons'
import { TFunction } from 'i18next'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { ButtonGroup } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import AppContext from '../../Authentication/AppContext'
import Authorization from '../../Authentication/Authorization'
import AuthService from '../../Authentication/AuthService'
import DialogContext from '../../Authentication/DialogContext'
import { ConfirmDialogProps } from '../../components/ConfirmDialog'
import DataTable, { CaptionButton } from '../../components/DataTable'
import { EditDialogProps } from '../../components/EditDialog'
import { FlipCard } from '../../components/FlipCard'
import ProtectedButton from '../../components/ProtectedButton'
import Loading from '../../layout/Loading'
import ActiveDirectoryService from '../../service/ActiveDirectoryService'
import GatewayType from '../GatewayType'

const ActiveDirectory: GatewayType = () => {
  const { t } = useTranslation()
  const [adConfig, setAdConfig] = useState({} as any)
  const [loading, setLoading] = useState(true)
  const { editDialogPropsSetter, confirmDialogPropsSetter } = useContext(DialogContext)

  useEffect(() => {
    ActiveDirectoryService.getAdConfig(setAdConfig).finally(() => setLoading(false))
  }, [])

  const columns = useMemo(() => {
    return [
      {
        text: 'label',
        dataField: 'label',
        classes: 'font-weight-bolder',
        sort: true,
      },
      {
        text: 'value',
        dataField: 'value',
        sort: true,
      },
    ]
  }, [])

  const data = Object.entries(adConfig).map((attr) => {
    return {
      label: t(`${attr[0]}.text`),
      value: getValue(attr[0], attr[1]),
    }
  })

  var captionButtons: CaptionButton[] = [
    {
      onClick: () => editAdConfigDialog(t, editDialogPropsSetter, setAdConfig, adConfig),
      text: t('configure'),
      icon: faEdit,
      permissions: Authorization.userManagement.activeDirectory.edit,
    },
  ]

  if (Object.keys(adConfig).length > 0) {
    captionButtons.push({
      onClick: () => deleteAdConfigDialog(t, confirmDialogPropsSetter, setAdConfig, adConfig),
      variant: 'outline-danger',
      icon: faTrashAlt,
      permissions: Authorization.userManagement.activeDirectory.edit,
    })
  }

  return (
    <Loading isLoading={loading}>
      <DataTable
        keyField='label'
        data={data}
        columns={columns}
        headerClasses='d-none'
        searchable={false}
        caption={t('adConfig.text')}
        helpLink={t('adConfig.helpLink')}
        captionButtons={captionButtons}
      />
    </Loading>
  )
}

function getValue(field: string, value: unknown): unknown {
  if (field === 'password') {
    return '<Protected>'
  }
  if (field === 'certificate' && value instanceof Blob) {
    return value.size ? '<Certificate Uploaded>' : ''
  }
  return value
}

function deleteAdConfigDialog(t: TFunction, confirmDialogPropsSetter: any, setAdConfig: React.Dispatch<any>, adConfig: any) {
  const deleteDialogProps: ConfirmDialogProps = {
    show: true,
    header: t('adConfig.deleteHeader'),
    body: t('adConfig.deleteBody', { name: adConfig.name }),
    submitText: t('yes'),
    onSubmit: ActiveDirectoryService.deleteAdConfig,
    onSubmitExtraArgs: [t, setAdConfig],
    cancelText: t('no'),
  }
  confirmDialogPropsSetter(deleteDialogProps)
}

function editAdConfigDialog(t: TFunction, editDialogPropsSetter: any, setAdConfig: React.Dispatch<any>, adConfig: any) {
  const editDialogProps: EditDialogProps = {
    show: true,
    header: t('adConfig.editHeader'),
    onSubmit: ActiveDirectoryService.configureAdConfig,
    onSubmitExtraArgs: [t, setAdConfig],
    data: [
      {
        id: 'name',
        value: adConfig.name,
        type: 'text',
        label: t('name.text'),
        placeholder: t('name.text'),
        autoComplete: 'AdName',
        requiredMessage: t('name.inValidMessage'),
        required: true,
      },
      {
        id: 'address',
        value: adConfig.address,
        type: 'text',
        label: t('address.text'),
        placeholder: t('address.text'),
        autoComplete: 'AdAddress',
        requiredMessage: t('address.inValidMessage'),
        required: true,
      },
      {
        id: 'port',
        value: adConfig.port,
        type: 'number',
        label: t('port.text'),
        placeholder: t('port.text'),
        required: true,
        requiredMessage: t('port.inValidMessage'),
        min: '1',
        minInvalidMessage: t('port.rangeInValidMessage'),
        max: '65535',
        maxInvalidMessage: t('port.rangeInValidMessage'),
      },
      {
        id: 'certificate',
        value: adConfig.certificate,
        type: 'file',
        label: t('certificate.text'),
      },
      {
        id: 'searchBase',
        value: adConfig.searchBase,
        type: 'text',
        label: t('searchBase.text'),
        placeholder: t('searchBase.text'),
        autoComplete: 'AdSearchBase',
        requiredMessage: t('searchBase.inValidMessage'),
        required: true,
      },
      {
        id: 'userDN',
        value: adConfig.userDN,
        type: 'text',
        label: t('upn.text'),
        placeholder: t('upn.text'),
        autoComplete: 'AdUserDN',
        requiredMessage: t('upn.inValidMessage'),
        required: true,
      },
      {
        id: 'password',
        value: adConfig.password,
        type: 'password',
        label: t('password.text'),
        placeholder: t('password.text'),
        autoComplete: 'new-Password',
        requiredMessage: t('password.inValidMessage'),
        required: true,
      },
    ],
  }

  editDialogPropsSetter(editDialogProps)
}

export const AdConfigFlipCard = () => {
  const { t } = useTranslation()
  const [adConfig, setAdConfig] = useState({} as any)
  const { userPermissions } = useContext(AppContext)
  const { editDialogPropsSetter, confirmDialogPropsSetter } = useContext(DialogContext)
  const viewPermission = Authorization.userManagement.activeDirectory.view
  const editPermission = Authorization.userManagement.activeDirectory.edit

  useEffect(() => {
    if (AuthService.hasPermission(userPermissions, viewPermission)) {
      ActiveDirectoryService.getAdConfig(setAdConfig)
    }
  }, [userPermissions, viewPermission])

  return (
    <FlipCard
      permissions={viewPermission}
      headerText={t('adConfig.text')}
      headerLink='./userManagement/activeDirectory'
      flipFrontContent={
        <ButtonGroup vertical>
          <ProtectedButton
            permissions={editPermission}
            variant='link'
            className='m-1'
            onClick={() => editAdConfigDialog(t, editDialogPropsSetter, setAdConfig, adConfig)}>
            {t('configure')}
          </ProtectedButton>
          <ProtectedButton
            permissions={editPermission}
            variant='link'
            className='m-1'
            onClick={() => deleteAdConfigDialog(t, confirmDialogPropsSetter, setAdConfig, adConfig)}>
            {t('adConfig.deleteHeader')}
          </ProtectedButton>
        </ButtonGroup>
      }
    />
  )
}
export default ActiveDirectory
